import { useMemo } from 'react';
import { LayoutCarousel, Tooltip } from '@silverfort/sf-components';
import { splitArrayIntoBatches } from '../../utils';

const DEFAULT_BATCH_SIZE = 3;

export interface RiskyIndicator {
    name: string;
    value: number;
}

interface TopRiskIndicatorChartProps {
    title: string;
    list: RiskyIndicator[];
    isLoading: boolean;
}

const IndicatorRow = ({ indicator }: { indicator: RiskyIndicator }) => {
    return (
        <div className="flex flex-row items-center justify-between flex-1 bg-white min-h-10 max-h-10 w-full p-1 rounded-lg">
            <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 text-13 text-sf-gray-800">{indicator.name}</div>
            </div>
            <div className="text-12 bg-sf-white border border-sf-gray-100 rounded-[4px] px-2">
                <Tooltip placement='top' title='Hits'>
                    <>{indicator.value.toLocaleString()}</>
                </Tooltip>
            </div>
        </div>
    );
};

const LoadingRow = () => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            <div className="h-4 w-32 bg-sf-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded animate-pulse" />
        </div>
    </div>
);

const NoDataRow = ({ isFirstRow }: { isFirstRow?: boolean }) => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            <div className="text-13 text-sf-gray-500">{isFirstRow ? 'No data' : ''}</div>
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded" />
        </div>
    </div>
);

const TopRiskIndicatorChart = ({ title, list, isLoading }: TopRiskIndicatorChartProps) => {
    const batches = useMemo(() => splitArrayIntoBatches(list, DEFAULT_BATCH_SIZE), [list]);

    const pages = useMemo(() => {
        if (isLoading) {
            return [
                <div key="loading" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <LoadingRow key={index} />
                    ))}
                </div>
            ];
        }

        if (list.length === 0) {
            return [
                <div key="no-data" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <NoDataRow key={index} isFirstRow={index === 0} />
                    ))}
                </div>
            ];
        }

        const pagesArray: React.ReactNode[] = batches.map((batch, index) => (
            <div key={index} className="flex flex-1 flex-col w-full gap-2 items-start">
                {batch.map((indicator) => (
                    <IndicatorRow key={indicator.name} indicator={indicator} />
                ))}
            </div>
        ));
        return pagesArray;
    }, [isLoading, list, batches]);

    return <LayoutCarousel title={title} pages={pages} contentClassName="flex flex-1 flex-col justify-center px-6 flex-shrink-0 h-full min-h-[150px]" />;
};

export default TopRiskIndicatorChart;
